import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import {
  AlertCircle,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Pencil,
  Send,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import type { StatusTone } from '@/components/ui/status-badge';

export interface EmailStatusConfig {
  tone: StatusTone;
  icon: LucideIcon;
  label: MessageDescriptor;
}

// Registered as MessageDescriptors so the Lingui extractor sees the explicit IDs.
// Rendered by EmailStatusBadge (EmailDeliveryStatus, EmailCockpitPage); keep in sync if a status is added.
export const EMAIL_STATUS: Record<string, EmailStatusConfig> = {
  draft: { tone: 'neutral', icon: Pencil, label: msg({ id: 'invoice.email.status.draft', message: 'Draft' }) },
  approved: { tone: 'info', icon: Clock, label: msg({ id: 'invoice.email.status.approved', message: 'Approved' }) },
  rejected: { tone: 'danger', icon: XCircle, label: msg({ id: 'invoice.email.status.rejected', message: 'Rejected' }) },
  discarded: { tone: 'neutral', icon: Ban, label: msg({ id: 'invoice.email.status.discarded', message: 'Discarded' }) },
  queued: { tone: 'neutral', icon: Clock, label: msg({ id: 'invoice.email.status.queued', message: 'Queued' }) },
  sending: { tone: 'info', icon: Send, label: msg({ id: 'invoice.email.status.sent', message: 'Sent' }) },
  sent: { tone: 'info', icon: Send, label: msg({ id: 'invoice.email.status.sent', message: 'Sent' }) },
  delivered: { tone: 'success', icon: CheckCircle2, label: msg({ id: 'invoice.email.status.delivered', message: 'Delivered' }) },
  bounced: { tone: 'danger', icon: AlertCircle, label: msg({ id: 'invoice.email.status.bounced', message: 'Bounced' }) },
  failed: { tone: 'danger', icon: AlertCircle, label: msg({ id: 'invoice.email.status.failed', message: 'Failed' }) },
  complained: { tone: 'warning', icon: AlertTriangle, label: msg({ id: 'invoice.email.status.complained', message: 'Complained' }) },
};

/** An unknown status reads as queued: the log exists but has no delivery event yet. */
export function emailStatusConfig(status: string | undefined): EmailStatusConfig {
  return (status && EMAIL_STATUS[status]) || EMAIL_STATUS.queued;
}
